/**
 * Demo portal entry point — /demo
 *
 * Fetches (or creates) the visitor's ephemeral account from the server,
 * then navigates to their personal order page at
 * /org/demo-public/order/{customUrl}.
 *
 * The URL in the address bar changes to the org-scoped URL on first visit.
 * This is the pragmatic trade-off: the SPA's org context is URL-driven,
 * and shimming a fake location would require duplicating CustomerEntry.
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { track } from "@/lib/analytics";

interface DemoAccountInfo {
  orgSlug: string;
  customUrl: string;
}

export default function DemoPortal() {
  const [, setLocation] = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function initDemo() {
      try {
        track("demo_started");
        const res = await fetch("/demo/account-info", { credentials: "include" });

        if (!res.ok) {
          if (res.status === 429) {
            setError(
              "The demo is temporarily unavailable due to high traffic. Please try again in an hour."
            );
          } else {
            setError("Failed to start the demo. Please refresh the page.");
          }
          return;
        }

        const { orgSlug, customUrl }: DemoAccountInfo = await res.json();

        if (!cancelled) {
          // Navigate to the customer order page for this ephemeral account.
          // replace: true so the back button doesn't loop back to /demo.
          setLocation(`/org/${orgSlug}/order/${customUrl}`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError("Failed to start the demo. Please check your connection and refresh.");
        }
      }
    }

    initDemo();
    return () => { cancelled = true; };
  }, [setLocation]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-4xl mb-4">🥬</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Demo Unavailable</h1>
          <p className="text-gray-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Starting your demo…</p>
      </div>
    </div>
  );
}
